import React, { useState, useEffect } from 'react';

import pricingBunny from '~/images/bunnynet-pricing.svg';
import { PricingTierField } from './pricing_fields';
import { getStyleTransform } from '~/utils/helpers';

interface PricingCdnProps {
  block: {
    settings?: Settings;
    _template?: string;
    css_class_name?: string;
    region?: string;
    region_label?: string;
    cta_contact?: CtaField;
    cta_field?: CtaField;
    text?: any;
    theme?: string;
    offset?: OffsetSettings;
    scale?: ScaleSettings;
  };
  vars: {
    pricing_standard_eu_na_gb: string;
    pricing_standard_as_oc_gb: string;
    pricing_standard_sa_gb: string;
    pricing_standard_me_af_gb: string;
    pricing_volume_500tb_gb: string;
    pricing_volume_500tb_1pb_gb: string;
    pricing_volume_1pb_2pb_gb: string;
    pricing_standard_limit: string;
    pricing_volume_limit: string;
  };
}

const PricingCdn = ({ block, vars }: PricingCdnProps) => {
  const pricing_standard_eu_na_gb = Number(vars.pricing_standard_eu_na_gb);
  const pricing_standard_as_oc_gb = Number(vars.pricing_standard_as_oc_gb);
  const pricing_standard_sa_gb = Number(vars.pricing_standard_sa_gb);
  const pricing_standard_me_af_gb = Number(vars.pricing_standard_me_af_gb);
  const pricing_volume_500tb_gb = Number(vars.pricing_volume_500tb_gb);
  const pricing_volume_500tb_1pb_gb = Number(vars.pricing_volume_500tb_1pb_gb);
  const pricing_volume_1pb_2pb_gb = Number(vars.pricing_volume_1pb_2pb_gb);
  const pricing_standard_limit = Number(vars.pricing_standard_limit);
  const pricing_volume_limit = Number(vars.pricing_volume_limit);
  const imageStyleOverride = getStyleTransform(block.offset, block.scale);
  const themeClass = block.theme ? `pricing--${block.theme}` : '';
  const regionClass =
    !block.region || block.region === 'All' ? 'all-regions' : 'single-region';

  if (!block.region) {
    block.region = 'All';
  }

  let default_EuNa_Gb = 0;
  if (
    block.region === 'All' ||
    block.region === 'Europe' ||
    block.region === 'North America'
  ) {
    default_EuNa_Gb = 200;
  }

  let default_AsOc_Gb = 0;
  if (block.region === 'All') {
    default_AsOc_Gb = 50;
  }
  if (block.region === 'Australia' || block.region === 'Asia') {
    default_AsOc_Gb = 200;
  }

  let default_Sa_Gb = 0;
  if (block.region === 'Latam') {
    default_Sa_Gb = 200;
  }

  let default_MeAf_Gb = 0;
  if (block.region === 'Africa') {
    default_MeAf_Gb = 200;
  }

  const defaultStandardPrice =
    default_EuNa_Gb * pricing_standard_eu_na_gb +
    default_AsOc_Gb * pricing_standard_as_oc_gb +
    default_Sa_Gb * pricing_standard_sa_gb +
    default_MeAf_Gb * pricing_standard_me_af_gb;

  const defaultVolumePrice =
    (default_EuNa_Gb + default_AsOc_Gb + default_Sa_Gb + default_MeAf_Gb) *
    pricing_volume_500tb_gb;

  const [rangeValue_EuNa_Gb, setRangeValueState_EuNa] =
    useState(default_EuNa_Gb);
  const [rangeValue_AsOc_Gb, setRangeValueState_AsOc] =
    useState(default_AsOc_Gb);
  const [rangeValue_Sa_Gb, setRangeValueState_Sa] = useState(default_Sa_Gb);
  const [rangeValue_MeAf_Gb, setRangeValueState_MeAf] =
    useState(default_MeAf_Gb);

  const [standardPriceValue, setStandardPrice] = useState(defaultStandardPrice);
  const [volumePriceValue, setVolumePrice] = useState(defaultVolumePrice);

  function calculateStandardPrice() {
    let standardPrice = 0;

    if (selectedUnitValue === 'TB') {
      standardPrice =
        rangeValue_EuNa_Gb * pricing_standard_eu_na_gb * 1000 +
        rangeValue_AsOc_Gb * pricing_standard_as_oc_gb * 1000 +
        rangeValue_Sa_Gb * pricing_standard_sa_gb * 1000 +
        rangeValue_MeAf_Gb * pricing_standard_me_af_gb * 1000;
    } else {
      standardPrice =
        rangeValue_EuNa_Gb * pricing_standard_eu_na_gb +
        rangeValue_AsOc_Gb * pricing_standard_as_oc_gb +
        rangeValue_Sa_Gb * pricing_standard_sa_gb +
        rangeValue_MeAf_Gb * pricing_standard_me_af_gb;
    }

    if (standardPrice < 1) {
      standardPrice = 1;
    }

    return Math.round(standardPrice * 100) / 100;
  }

  function calculateVolumePrice() {
    let volumeSum =
      Number(rangeValue_EuNa_Gb) +
      Number(rangeValue_AsOc_Gb) +
      Number(rangeValue_Sa_Gb) +
      Number(rangeValue_MeAf_Gb);
    let volumePrice = 0;

    if (selectedUnitValue === 'TB') {
      volumeSum = volumeSum * 1000;
    }

    if (volumeSum <= 500000) {
      volumePrice = volumeSum * pricing_volume_500tb_gb;
    } else if (volumeSum > 500000 && volumeSum <= 1000000) {
      volumePrice = volumeSum * pricing_volume_500tb_1pb_gb;
    } else if (volumeSum > 1000000 && volumeSum <= 2000000) {
      volumePrice = volumeSum * pricing_volume_1pb_2pb_gb;
    } else {
      volumePrice = volumeSum * pricing_volume_1pb_2pb_gb;
    }

    if (volumePrice < 1) {
      volumePrice = 1;
    }

    return Math.round(volumePrice * 100) / 100;
  }

  function calculateVolumeSum() {
    let volumeSum =
      Number(rangeValue_EuNa_Gb) +
      Number(rangeValue_AsOc_Gb) +
      Number(rangeValue_Sa_Gb) +
      Number(rangeValue_MeAf_Gb);

    if (selectedUnitValue === 'TB') {
      volumeSum = volumeSum * 1000;
    }

    const sumOverMax =
      volumeSum - 200000 || standardPriceValue - 2000 || volumePriceValue - 975;
    return sumOverMax;
  }

  const maxGb = 1000;
  const maxTb = 250; // Checked

  const [checked, setChecked] = useState(false);
  const [selectedUnitValue, setSelectedUnit] = useState('GB');
  const [maxUnitValue, setMaxUnit] = useState(maxGb);

  const toggleUnit = () => {
    setChecked(!checked);
    setMaxUnit(checked ? maxGb : maxTb);
    setSelectedUnit(checked ? 'GB' : 'TB');

    setStandardPrice(calculateStandardPrice());
    setVolumePrice(calculateVolumePrice());
  };

  useEffect(() => {
    const standardPrice = calculateStandardPrice();
    const volumePrice = calculateVolumePrice();
    setStandardPrice(standardPrice);
    setVolumePrice(volumePrice);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateStandardPrice(), calculateVolumePrice()]);

  return (
    <section
      className={`block block__pricing_cdn ${block.css_class_name} ${regionClass} ${themeClass}`}
    >
      <div className='container'>
        <div className='row'>
          <div className='col-lg-9'>
            <div className='pricing-wr'>
              <div className='pricing-header'>
                <input
                  id='pricing_switch'
                  type='checkbox'
                  name='pricing_switch'
                  onChange={toggleUnit}
                />
                <label htmlFor='pricing_switch'></label>
              </div>
              <div className='pricing-main'>
                <div className='row'>
                  {(block.region === 'All' ||
                    block.region === 'Europe' ||
                    block.region === 'North America') && (
                    <div className='col-md-6'>
                      <div className='card pricing-eu_na'>
                        <div className='top'>
                          {block.region_label ? (
                            <span className='title'>{block.region_label}</span>
                          ) : (
                            <span className='title'>EU/NA</span>
                          )}{' '}
                          <span className='size'>
                            {selectedUnitValue === 'TB' &&
                            rangeValue_EuNa_Gb >= maxTb
                              ? maxTb + '+'
                              : rangeValue_EuNa_Gb}{' '}
                            {selectedUnitValue}
                          </span>
                        </div>
                        <div className='slidecontainer'>
                          <input
                            id='range-eu_na'
                            type='range'
                            defaultValue={default_EuNa_Gb}
                            className='slider'
                            min='0'
                            max={maxUnitValue}
                            onChange={event =>
                              setRangeValueState_EuNa(event.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {(block.region === 'All' ||
                    block.region === 'Asia' ||
                    block.region === 'Australia') && (
                    <div className='col-md-6'>
                      <div className='card pricing-as_oc'>
                        <div className='top'>
                          {block.region_label ? (
                            <span className='title'>{block.region_label}</span>
                          ) : (
                            <span className='title'>Asia/Oceania</span>
                          )}{' '}
                          <span className='size'>
                            {selectedUnitValue === 'TB' &&
                            rangeValue_AsOc_Gb >= maxTb
                              ? maxTb + '+'
                              : rangeValue_AsOc_Gb}{' '}
                            {selectedUnitValue}
                          </span>
                        </div>
                        <div className='slidecontainer'>
                          <input
                            id='range-as_oc'
                            type='range'
                            defaultValue={default_AsOc_Gb}
                            className='slider'
                            min='0'
                            max={maxUnitValue}
                            onChange={event =>
                              setRangeValueState_AsOc(event.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {(block.region === 'All' || block.region === 'Latam') && (
                    <div className='col-md-6'>
                      <div className='card pricing-sa'>
                        <div className='top'>
                          {block.region_label ? (
                            <span className='title'>{block.region_label}</span>
                          ) : (
                            <span className='title'>South America</span>
                          )}{' '}
                          <span className='size'>
                            {selectedUnitValue === 'TB' &&
                            rangeValue_Sa_Gb >= maxTb
                              ? maxTb + '+'
                              : rangeValue_Sa_Gb}{' '}
                            {selectedUnitValue}
                          </span>
                        </div>
                        <div className='slidecontainer'>
                          <input
                            id='range-sa'
                            type='range'
                            defaultValue={default_Sa_Gb}
                            className='slider'
                            min='0'
                            max={maxUnitValue}
                            onChange={event =>
                              setRangeValueState_Sa(event.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {(block.region === 'All' || block.region === 'Africa') && (
                    <div className='col-md-6'>
                      <div className='card pricing-me_af'>
                        <div className='top'>
                          {block.region_label ? (
                            <span className='title'>{block.region_label}</span>
                          ) : (
                            <span className='title'>ME & Africa</span>
                          )}{' '}
                          <span className='size'>
                            {selectedUnitValue === 'TB' &&
                            rangeValue_MeAf_Gb >= maxTb
                              ? maxTb + '+'
                              : rangeValue_MeAf_Gb}{' '}
                            {selectedUnitValue}
                          </span>
                        </div>
                        <div className='slidecontainer'>
                          <input
                            id='range-me_af'
                            type='range'
                            defaultValue={default_MeAf_Gb}
                            className='slider'
                            min='0'
                            max={maxUnitValue}
                            onChange={event =>
                              setRangeValueState_MeAf(event.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='pricing-footer'>
                <PricingTierField
                  cta_contact={block.cta_contact}
                  cta_field={block.cta_field}
                  text={block.text}
                  standardPrice={standardPriceValue}
                  volumePrice={volumePriceValue}
                  standardLimit={pricing_standard_limit}
                  volumeLimit={pricing_volume_limit}
                />
              </div>
            </div>
          </div>
        </div>

        <img
          src={pricingBunny}
          alt='Pricing - bunny.net'
          title='Pricing - bunny.net'
          style={imageStyleOverride}
        />
      </div>
    </section>
  );
};

export default PricingCdn;
